import React, { useEffect, useMemo, useState, useRef, forwardRef } from 'react';
/**
 * ClockRenderer — Analog/digital clock reading with time input.
 *
 * DDD co-locatie: moved from components/practice/renderers/ in s67.
 */
import { QuestionRendererProps } from './types';
import { AnalogClock } from '../../../visuals';
import { Clock, Sun, Moon } from 'lucide-react';
import { error as logError } from '../../../utils/logger';
import { useEdgeTriggeredSubmit } from './useEdgeTriggeredSubmit';
export function ClockRenderer({
  question,
  onAnswer,
  onReadyChange,
  shouldValidate
}: QuestionRendererProps) {
  const clockConfig = question.data?.clock;
  const targetHours = clockConfig?.target?.hours || 0;
  const targetMinutes = clockConfig?.target?.minutes || 0;
  const targetSeconds = 0;
  const format = clockConfig?.target?.format || '24h';
  const mode = clockConfig?.mode || 'read_analog_fill';
  const showDigitalClock = mode === 'read_digital_fill';
  const timeOfDay = targetHours >= 6 && targetHours < 18 ? 'day' : 'night';
  const TimeIcon = timeOfDay === 'day' ? Sun : Moon;
  const showSeconds = targetSeconds !== 0;
  const [hours, setHours] = useState('');
  const [minutes, setMinutes] = useState('');
  const [seconds, setSeconds] = useState('');
  const hoursInputRef = useRef<HTMLInputElement>(null);
  const minutesInputRef = useRef<HTMLInputElement>(null);
  const secondsInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    setHours('');
    setMinutes('');
    setSeconds('');
    hoursInputRef.current?.focus();
  }, [question.id]);
  useEffect(() => {
    const isReady = hours !== '' && minutes !== '' && (targetSeconds === 0 ? true : seconds !== '');
    onReadyChange?.(isReady);
  }, [hours, minutes, seconds, targetSeconds, onReadyChange]);
  const answerPayload = useMemo(() => {
    const h = hours.padStart(2, '0');
    const m = minutes.padStart(2, '0');
    return showSeconds ? `${h}:${m}:${seconds.padStart(2, '0')}` : `${h}:${m}`;
  }, [hours, minutes, seconds, showSeconds]);
  useEdgeTriggeredSubmit(answerPayload, shouldValidate, onAnswer, question.id);
  // Guard: missing config — rendered AFTER all hooks
  if (!clockConfig || !clockConfig.target) {
    logError('ClockRenderer: Missing clock config in question.data.clock');
    return <div className="text-center p-8 bg-red-50 rounded-xl border-2 border-red-200" data-id="element-11348" data-mp="1dg9itcvwr7eq">
        <p className="text-red-700 font-bold" data-id="element-11349" data-mp="1hhl8fies7smx">⚠️ Klok configuratie ontbreekt</p>
        <p className="text-sm text-red-600 mt-2" data-id="element-11350" data-mp="kqstwp7hy8n9">
          Deze vraag heeft geen geldige klok data.
        </p>
      </div>;
  }
  const handleInputChange = (setter: React.Dispatch<React.SetStateAction<string>>, value: string, max: number, nextRef?: React.RefObject<HTMLInputElement>) => {
    if (value === '' || /^\d+$/.test(value) && parseInt(value) <= max) {
      setter(value);
      // Auto-advance focus once the field has 2 digits typed.
      if (value.length === 2 && nextRef?.current) {
        nextRef.current.focus();
        nextRef.current.select?.();
      }
    }
  };
  return <div className="flex flex-col items-center w-full max-w-2xl mx-auto space-y-6" data-id="element-11351" data-mp="2ka77uv7kwsoz">
      {/* Time of Day Indicator */}
      <div className="flex items-center gap-3 bg-indigo-50 px-6 py-3 rounded-2xl border-2 border-indigo-200 shadow-sm" data-id="element-11352" data-mp="2ai9ubht3lon3">
        <TimeIcon className={`w-6 h-6 ${timeOfDay === 'day' ? 'text-amber-500' : 'text-indigo-400'}`} data-id="element-11353" data-mp="26hhnev7p7vtz" />
        <span className="text-base font-bold text-slate-700" data-id="element-11354" data-mp="21iehz9nt3nlh">
          {timeOfDay === 'day' ? 'Overdag (06:00 - 18:00)' : "'s Nachts (18:00 - 06:00)"}
        </span>
      </div>

      {/* Clock Display */}
      {showDigitalClock ? <div className="p-8 bg-slate-800 rounded-3xl border-4 border-slate-700 shadow-2xl" data-id="element-11355" data-mp="1ni3py3arg2yq">
          <div className="font-mono text-6xl font-black text-emerald-400 tracking-wider" data-id="element-11356" data-mp="3lxgib4sb1vca">
            {targetHours.toString().padStart(2, '0')}:
            {targetMinutes.toString().padStart(2, '0')}
          </div>
        </div> : <div className="p-6 bg-slate-50 rounded-3xl border-2 border-slate-200 shadow-xl" data-id="element-11357" data-mp="3p0cucfrrfj59">
          <AnalogClock hours={targetHours} minutes={targetMinutes} seconds={targetSeconds} showSeconds={showSeconds} size={180} data-id="element-11358" data-mp="3tx3a11znkcq5" />
        </div>}

      {/* Time Input */}
      <div className="w-full bg-white p-6 rounded-2xl border-2 border-indigo-100 shadow-lg" data-id="element-11359" data-mp="3k1t6g9tlvxa9">
        <div className="flex items-center justify-between mb-5" data-id="element-11360" data-mp="366h0t7dwwmek">
          <div className="flex items-center gap-2" data-id="element-11361" data-mp="3b022paimm0j1">
            <div className="p-2 bg-indigo-50 rounded-xl" data-id="element-11362" data-mp="2wae26bi9r5wd">
              <Clock className="w-5 h-5 text-indigo-600" data-id="element-11363" data-mp="2wbyvjxsukisn" />
            </div>
            <span className="text-sm font-bold text-slate-500 uppercase tracking-wide" data-id="element-11364" data-mp="31aigbuvpfojf">
              Vul de tijd in
            </span>
          </div>
          <div className="px-3 py-1.5 bg-indigo-50 rounded-full border border-indigo-200" data-id="element-11365" data-mp="2nc227iviivr9">
            <span className="text-xs font-bold text-indigo-700 uppercase tracking-wide" data-id="element-11366" data-mp="2savhngud0wu2">
              {format === '24h' ? '24-uurs' : '12-uurs'}
            </span>
          </div>
        </div>

        <div className="flex items-center justify-center gap-3" data-id="element-11367" data-mp="23q0i72xv3nlg">
          <TimeInputField ref={hoursInputRef} label="Uur" value={hours} onChange={v => handleInputChange(setHours, v, 23, minutesInputRef)} data-id="element-11368" data-mp="2hogmkn89520f" />
          <div className="text-3xl font-black text-slate-400 mt-8" data-id="element-11369" data-mp="2rjs237rhid98">:</div>
          <TimeInputField ref={minutesInputRef} label="Min" value={minutes} onChange={v => handleInputChange(setMinutes, v, 59, showSeconds ? secondsInputRef : undefined)} data-id="element-11370" data-mp="2mlyymmhkfxcn" />
          {showSeconds && <>
              <div className="text-3xl font-black text-slate-400 mt-8" data-id="element-11371" data-mp="35eax2y53mehi">:</div>
              <TimeInputField ref={secondsInputRef} label="Sec" value={seconds} onChange={v => handleInputChange(setSeconds, v, 59)} data-id="element-11372" data-mp="230ddfp91d45t" />
            </>}
        </div>

        <p className="text-center text-slate-500 text-sm mt-4 font-medium" data-id="element-11373" data-mp="2byfa5slqlana">
          Typ de tijd zoals je die op de klok ziet
        </p>
      </div>
    </div>;
}
/** Small reusable time input field to reduce JSX repetition. */
const TimeInputField = forwardRef<HTMLInputElement, {
  label: string;
  value: string;
  onChange: (v: string) => void;
}>(({
  label,
  value,
  onChange
}, ref) => <div className="flex flex-col items-center" data-id="element-11374" data-mp="2vmelp5o4kltr">
    <label className="text-xs font-bold text-slate-500 mb-2 uppercase tracking-wide" data-id="element-11375" data-mp="2zqdz3fe3z4iw">
      {label}
    </label>
    <input ref={ref} type="text" inputMode="numeric" pattern="[0-9]*" value={value} onChange={e => onChange(e.target.value.replace(/\D/g, ''))} onPaste={e => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '');
    onChange(pasted.slice(0, 2));
  }} placeholder="00" className="w-16 h-16 text-center text-2xl font-black rounded-2xl border-2 border-slate-200 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 outline-none transition-all placeholder:text-slate-300 bg-slate-50" maxLength={2} data-id="element-11376" data-mp="11bjd43cxx6qz" />
  </div>);